/**
 * The page's uncaught errors and unhandled rejections, sent to our own API (ADR 0035 D10; ADR 0008: no error service,
 * no third-party origin): `POST /api/client-errors` on this origin, at most 5 per page load, and nothing else ever (no
 * console line, no analytics). The account's token goes along when there is one, so the server's log line names the
 * account; the server never needs it. A report that cannot be sent is dropped: it must never raise an error of its own.
 */
import type { ClientError } from '@ab/api-contract';

/** At most this many reports per page load (a loop of errors must not become a loop of requests). */
const perPage = 5;

/** Text the contract accepts: no NUL, at most `max` characters. */
const clip = (text: string, max: number): string => text.replaceAll('\u0000', '').slice(0, max);

export type ClientErrorSource = {
  /** The session's token, or null (the report is sent without it). */
  readonly token: () => string | null;
  /** The locale the page shows. */
  readonly locale: () => string;
};

/** Listens for the page's uncaught errors and unhandled rejections and reports the first five. */
export const reportClientErrors = (source: ClientErrorSource): void => {
  let sent = 0;
  const send = (message: string, stack: string | undefined): void => {
    if (sent >= perPage) return;
    sent += 1;
    const build = import.meta.env.VITE_AB_BUILD;
    const body: ClientError = {
      message: clip(message, 1000) || 'error',
      // The page without its query string or fragment: nothing a link carries (an invite code, later) reaches the log.
      url: clip(location.origin + location.pathname, 500),
      locale: source.locale(),
      ...(stack === undefined ? {} : { stack: clip(stack, 4000) }),
      ...(build === undefined || build === '' ? {} : { build: clip(build, 64) }),
    };
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = source.token();
    if (token !== null) headers['authorization'] = `Bearer ${token}`;
    try {
      void fetch('/api/client-errors', { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => undefined);
    } catch {
      // Nothing to do: a report that cannot leave is dropped.
    }
  };
  window.addEventListener('error', (event) => {
    const error: unknown = event.error;
    send(event.message, error instanceof Error ? error.stack : undefined);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    send(`unhandled rejection: ${reason instanceof Error ? reason.message : String(reason)}`, reason instanceof Error ? reason.stack : undefined);
  });
};
